import {EventModelState} from "src/state/slice/EventModelSlice";
import {EventModelStatus, HttpMethod, Stage} from "../enum/enums";
import {backendServiceClient} from "./backendServiceClient";
import {
  AttributeRule,
  CompleteS3FileUploadAttribute,
  ContentAndFieldDetails,
  ContentDetailsModel,
  CreateContentsModel,
  CreateContentsResponse,
  EventModel,
  ExecuteEventsResponse,
  FieldNameModel,
  IContextAttribute,
  IGetEventModelResponse,
  InitiateMultipartUploadAttribute,
  PromoteDerivedSegmentationAttributes,
  S3CompletedPart,
  UploadFileToS3Attribute,
  UpsertDerivedSegmentationAttribute
} from "src/entity/entities";
import {
  getStageBasedAssetManagementServiceEndpoint,
  getStageBasedEASEEndpoint,
  getStageBasedSSEndpoint
} from "../constant/endpoints";
import {AxiosError, AxiosResponse} from "axios";
import {APIPaths, CommonConstants, RetryConstants} from "src/constant/constants";
import {AEMClient, createFormInput, getStage} from "src/util/commonUtil";
import {logger} from "src/logger";

export const getTriggersSolutionTypes = async (stage: Stage): Promise<string[]> => {
  return backendServiceClient({
    url: `${getStageBasedEASEEndpoint(getEASEBackendStageToBeCalled(stage))}${APIPaths.getTriggersAndSolutionTypes}`,
    method: HttpMethod.GET,
  })
    .then((response) => {
      return response.data?.triggerAndSolutionTypeList;
    })
    .catch((error) => {
      logger.error("error in getTriggersAndSolutionTypes API = " + error);
      throw error;
    });
}

/**
 * Calls the GetEventModels EASE API and returns a list of event models.
 * @param stage backend stage whose API will be called
 * @returns promise resolving to list of event models
 */
export const getEventModels = async (stage: Stage): Promise<string[]> => {
  return backendServiceClient({
    url: `${getStageBasedEASEEndpoint(getEASEBackendStageToBeCalled(stage))}${APIPaths.GetEventModels}`,
    method: HttpMethod.GET,
  })
    .then((response) => {
      return response.data?.eventModels;
    })
    .catch((error) => {
      logger.error("error in getEventModels API = " + error);
      throw error;
    });
}

/**
 * Calls the GetEventModel EASE API and returns the definition of the event model.
 * @param eventName name of the event model to fetch
 * @param eventStatus status of the event model that needs to be fetched
 * @param stage backend stage whose API will be called
 * @returns promise resolving to event model definition
 */
export const getEventModel = async (eventName: string, eventStatus: EventModelStatus, stage: Stage): Promise<EventModelState> => {
  return backendServiceClient({
    url: `${getStageBasedEASEEndpoint(getEASEBackendStageToBeCalled(stage))}${APIPaths.GetEventModel}/${eventName}`,
    method: HttpMethod.GET,
    queryParams: {
      "status": eventStatus,
    },
  })
    .then((response) => {
      const getEventModelResponse: IGetEventModelResponse = response.data;

      return {
        bindleId: getEventModelResponse?.bindleId,
        clientUseCase: getEventModelResponse?.eventModel?.clientUseCase,
        eventName: getEventModelResponse?.eventModel?.eventName,
        eventRule: JSON.stringify(JSON.parse(getEventModelResponse?.eventModel?.eventRule), null, 4),
        eventSolution: {
          type: getEventModelResponse?.eventModel?.eventSolution?.type,
          solutionDetailObject: getEventModelResponse?.eventModel?.eventSolution?.solutionDetail,
        },
        validFrom: getEventModelResponse?.eventModel?.validFrom,
        validTill: getEventModelResponse?.eventModel?.validTill
      } as EventModelState;
    })
    .catch((error) => {
      logger.error("error in getEventModel API = " + error);
      throw error;
    });
}

/**
 * Calls the UpsertEventModel EASE API to upsert an event model in backend.
 * @param eventModel event model definition to be passed as payload
 * @param eventStatus event model's status
 * @param stage backend stage whose API will be called
 */
export const upsertEventModel = async (eventModel: EventModelState, eventStatus: EventModelStatus, stage: Stage): Promise<AxiosResponse> => {
  let eventModelToUpsert: EventModel;
  try {
    const eventRuleToUpsert = eventModel?.eventRule ? JSON.stringify(JSON.parse(eventModel?.eventRule)) : undefined;
    const eventSolutionDetailToUpsert = eventModel?.eventSolution?.solutionDetailObject;
    eventModelToUpsert = {
      clientUseCase: eventModel?.clientUseCase,
      eventName: eventModel?.eventName,
      status: eventStatus,
      eventRule: eventRuleToUpsert,
      validFrom: eventModel?.validFrom,
      validTill: eventModel?.validTill,
      eventSolution: {
        type: eventModel?.eventSolution?.type,
        solutionDetail: eventSolutionDetailToUpsert,
      },
      contentId: eventModel?.contentId,
      contentPath: eventModel?.contentPath
    }
  } catch (error) {
    logger.error("error in creating request for upsertEventModel API = " + error);
    return Promise.reject(error);
  }

  return backendServiceClient({
    url: `${getStageBasedEASEEndpoint(getEASEBackendStageToBeCalled(stage))}${APIPaths.UpsertEventModel}`,
    method: HttpMethod.POST,
    body: {
      bindleId: eventModel?.bindleId,
      eventModel: eventModelToUpsert
    }
  })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      logger.error("error in upsertEventModel API = " + error);
      throw error;
    });
}

/**
 * Calls the FetchContextAttributes EASE API and returns all context attributes
 * @param stage backend stage whose API will be called
 * @returns promise resolving to context attributes defined in EASE
 */
export const fetchContextAttributes = async (stage: Stage): Promise<IContextAttribute[]> => {
  return backendServiceClient({
    url: `${getStageBasedEASEEndpoint(getEASEBackendStageToBeCalled(stage))}${APIPaths.FetchContextAttributes}`,
    method: HttpMethod.POST,
  })
      .then((response) => {
        return response.data?.contextAttributes as IContextAttribute[];
      })
      .catch((error) => {
        logger.error("error in fetchContextAttributes API = " + error);
        throw error;
      });
}

/**
 * Calls the ExecuteEvents EASE API
 * @param clientUseCase use case for which execute events will be called
 * @param employeeId employee id for which execute events will be called
 * @param requestContext request context object passed in request payload
 * @param stage backend stage whose API will be called
 * @returns promise resolving to execute events response
 */
export const executeEvents = 
  async (clientUseCase: string, employeeId: string,
    requestContext: Record<string, any>, stage: Stage): Promise<ExecuteEventsResponse> => {
  
  return backendServiceClient({
    url: `${getStageBasedEASEEndpoint(getEASEBackendStageToBeCalled(stage))}${APIPaths.ExecuteEvents}`,
    method: HttpMethod.POST,
    headers: {
      "x-employee-id": employeeId,
    },
    body: {
      clientUseCase: clientUseCase,
      requestContext: requestContext,
    }
  })
    .then((response) => {
      return response.data as ExecuteEventsResponse;
    })
    .catch((error) => {
      logger.error("error in executeEvents API = " + error);
      throw error;
    });
}

/**
 * Calls the GetProcessedEvents EASE API and returns a list of events for the particular triggerName.
 * @param triggerName Name of trigger
 * @param stage backend stage whose API will be called
 * @returns promise resolving to list of events
 */
export const getProcessedEvents = async (stage: Stage, triggerName: String) => {
  return backendServiceClient({
    url: `${getStageBasedEASEEndpoint(getEASEBackendStageToBeCalled(stage))}${APIPaths.GetProcessedEvents}${triggerName}`,
    method: HttpMethod.GET,
  })
    .then((response) => {
      return response.data?.processedEvents;
    })
    .catch((error) => {
      logger.error("Error in getProcessedEvents API = " + error);
      throw error;
    });
}

/**
 * Calls the RetryEvent EASE API
 * @param stage backend stage whose API will be called
 */
export const retryFailedEvents = async (stage: Stage, eventId: String) => {
  return backendServiceClient({
    url: `${getStageBasedEASEEndpoint(getEASEBackendStageToBeCalled(stage))}${APIPaths.RetryEvents}`,
    method: HttpMethod.POST,
    body: {
      ids: [eventId],
    }
  })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      logger.error("Error in retryEvent API = " + error);
      throw error;
    });
}
/**
 * Calls the ViewAttributeRule API of segmentation service to fetch the available data attributes.
 * @param stage backend stage whose API will be called
 * @returns promise resolving to a list of available data attributes in segmentation service
 */
export const getAvailableDataAttributesInSegmentationService = async (): Promise<AttributeRule[]> => {
  
  return backendServiceClient({
    url: `${getEndpointForSegmentationService(getStage() as Stage)}${APIPaths.ViewAttributeRule}`,
    method: HttpMethod.POST,
    body: {
      attributeRuleList: [],
    },
    withCredentials: true,
  })
    .then((response) => {
      return response.data?.attributeRuleList as AttributeRule[];
    })
    .catch((error) => {
      logger.error("error in viewAttributeRule API = " + error);
      throw error;
    });
}

/**
 * Calls the InitiateMultipartUpload API of Asset Management Service to initiate chunked upload of files
 * @param stage backend stage whose API will be called
 * @param clientId client uploading the file in the system
 * @param key the filename being uploaded
 * @returns promise resolving to the uploadId corresponding to subsequent parts being uploaded
 */
export const initiateMultipartUpload =
    async (stage: Stage, clientId: string, key: string): Promise<InitiateMultipartUploadAttribute> => {
      return backendServiceClient({
        url: `${getEndpointForAssetManagementService(stage)}${APIPaths.InitiateMultipartUpload}`,
        method: HttpMethod.POST,
        body: {
          clientId: clientId,
          key: key
        }
      }).then((response) => {
        return response.data as InitiateMultipartUploadAttribute
      }).catch((error) => {
        logger.error("Error occurred while initiating multipart upload: " + error);
        throw error;
      });
    }

/**
 * Calls the UploadFileToS3 API of Asset Management Service to upload each chunk to s3
 * @param stage backend stage whose API will be called
 * @param clientId client uploading the file in the system
 * @param key the filename being uploaded
 * @param uploadId the uploading session id generated from InitiateMultipartUpload API
 * @param partNumber the specific numerical sequence of the chunk being uploaded
 * @param partLength the length of the part being uploaded
 * @param inputFile the actual chunked file being uploaded (base64)
 * @returns promise resolving to the checksum and etag of the corresponding part being uploaded
 */
export const uploadFileToS3 =
    async (stage: Stage, clientId: string, key: string, uploadId: string,
           partNumber: number, partLength: number, inputFile: any): Promise<UploadFileToS3Attribute> => {
      return backendServiceClient({
        url: `${getEndpointForAssetManagementService(stage)}${APIPaths.UploadFileToS3}`,
        method: HttpMethod.POST,
        body: {
          clientId: clientId,
          key: key,
          uploadId: uploadId,
          partNumber: partNumber,
          partLength: partLength,
          inputFile: inputFile
        }
      }).then((response) => {
        return response.data as UploadFileToS3Attribute
      }).catch((error) => {
        logger.error("Error occurred while uploading file to s3: " + error)
        throw error;
      });
    }

/**
 * Calls the CompleteS3FileUpload API of Asset Management Service to stitch chunks uploaded in s3
 * @param stage backend stage whose API will be called
 * @param clientId client uploading the file in the system
 * @param key the filename being uploaded
 * @param uploadId the uploading session id generated from InitiateMultipartUpload API
 * @param parts the information about etag and checksum received from UploadFileToS3 operation
 * @returns promise resolving to the etag and checksum of the final stitched file
 */
export const completeS3FileUpload =
    async (stage: Stage, clientId: string, key: string,
           uploadId: string, parts: S3CompletedPart[]): Promise<CompleteS3FileUploadAttribute> => {
      return backendServiceClient({
        url: `${getEndpointForAssetManagementService(stage)}${APIPaths.CompleteS3FileUpload}`,
        method: HttpMethod.POST,
        body: {
          clientId: clientId,
          key: key,
          uploadId: uploadId,
          parts: parts
        }
      }).then((response) => {
        return response.data as CompleteS3FileUploadAttribute
      }).catch((error) => {
        logger.error("Error occurred while stitching uploaded s3 chunks: " + error)
        throw error;
      });
    }

/**
 * Calls the upsert derived segmentation attribute API of EASE to create derived attribute in SS
 * @param attributeName is the derived attribute name that will be created in SS
 * @param description is the description of the corresponding attribute name
 * @param variables is the list of employee attributes on which attribute rule is created
 * @param definition is the json logic definition of the attribute created
 * @param operationType defines whether the operation is CREATE / UPDATE operation
 * @returns promise resolving to attribute details created / updated
 */
export const upsertDerivedSegmentationAttribute = async (attributeName: string, description: string, variables: string[],
                                                         definition: string, operationType: string, retries: number = 0): Promise<UpsertDerivedSegmentationAttribute> => {
  const operation = () => backendServiceClient({
    url: `${getStageBasedEASEEndpoint(getEASEBackendStageToBeCalled(getStage() as Stage))}${APIPaths.UpsertDerivedSegmentationAttribute}`,
    method: HttpMethod.POST,
    body: {
      attributeName,
      description,
      variables,
      definition,
      operationType
    },
  }).then((response) => {
    return response.data as UpsertDerivedSegmentationAttribute;
  });
  return retryWithDelay(operation, 500);
}

/**
 * Calls the upsert derived segmentation attribute API of EASE to create derived attribute in SS
 * @param attributeList the list of attributes to be promoted in higer env of segmentation service
 * @param promoteToStage states whether the attribute needs to be promoted in SS gamma / prod
 * @returns promise resolving to attributes successfully promoted
 */
export const promoteDerivedSegmentationAttributes = async (attributeList: string[], promoteToStage: Stage, retries: number = 0): Promise<PromoteDerivedSegmentationAttributes> => {
  const operation = () =>
      backendServiceClient({
        url: `${getStageBasedEASEEndpoint(getEASEBackendStageToBeCalled(promoteToStage))}${APIPaths.PromoteDerivedSegmentationAttributes}`,
        method: HttpMethod.POST,
        body: {
          attributeList,
          promoteToStage,
        },
      }).then((response) => {
        return response.data as PromoteDerivedSegmentationAttributes;
      });
  return retryWithDelay(operation, 500);
}

/**
 * This function returns the stage to be called for the EASE APIs.
 * We need to call EASE backend alpha from lower environments of EASE UI,
 * and beta and prod as required form the prod environment of EASE UI.
 */
const getEASEBackendStageToBeCalled = (stage: Stage) => {
  return getStage() === Stage.prod ? stage : Stage.alpha;
}

/**
 * Return endpoint to be called for Segmentation Service based on stage.
 * From prod/beta/gamma, we will call SS through TellUs Cloudfront, from local we will call SS APIG.
 */
const getEndpointForSegmentationService = (stage: Stage) => {
  if (stage === Stage.test) {
    return getStageBasedSSEndpoint(stage);
  }
  return window.location.origin;
}

/**
 * Return endpoint to be called for asset management service based on stage.
 * @param stage for which endpoint is required
 * @returns endpoint to be called if stage is test else returns domain name and calls are intercepted via cloudfront
 */
const getEndpointForAssetManagementService = (stage: Stage) => {
  if (stage === Stage.test) {
    return getStageBasedAssetManagementServiceEndpoint(stage);
  }
  return  window.location.origin;
}

/**
 * 
 * @param millis time in epochs
 * @returns Time in UTC 
 */
export const getTimeFromEpoch = (millis: string) => {
  return new Date(parseInt(millis)).toUTCString();
}

/**
 *
 * @param date time in UTC
 * @returns Time in epochs
 */
export const getEpochFromTime = (date: string) => {
  return (new Date(new Date(date).toISOString())).getTime();
}

/**
 * This function executes an operation with delay for a particular status code
 * @param operation is the operation that needs to be retried
 * @param statusCode is the status code for which retry needs to happen
 * @param retries is the number of retries that need to happen, bounded by RetryConstants.MAX_RETRIES
 */
const retryWithDelay = async <T>(
    operation: () => Promise<T>,
    statusCode: number,
    retries: number = 0
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === statusCode && retries < RetryConstants.MAX_RETRIES) {
      logger.info(`Retrying in ${RetryConstants.RETRY_DELAY} ms...`);
      await new Promise((resolve) => setTimeout(resolve, RetryConstants.RETRY_DELAY));
      return retryWithDelay(operation, retries + 1);
    } else {
      logger.error('Error:', axiosError);
      throw error;
    }
  }
};

/**
 * createAEMContent: Create AEM Content
 * @param input
 */
export async function createAEMContent(input) : Promise<CreateContentsResponse> {
  const formData = createFormInput(input);
  const config = {
    method: CommonConstants.HTTP_POST,
    url:  CommonConstants.AEM_STAGE_CREATE_CONTENTS_API
  };
  let contentId;
  let contentPath;
  await AEMClient(config, formData)
      .then((res)=>{
        contentId = res.data.contentId;
        contentPath = res.data.contentPath;
      });
  return {contentId: contentId, contentPath: contentPath} as CreateContentsResponse;
}

/**
 * buildAEMCreateContentsRequest: Builds Request for create Contents
 * @param EventModel
 */
export const buildAEMCreateContentsRequest = (EventModel: EventModelState) : CreateContentsModel => {
  let createContentsRequest: CreateContentsModel;
  try {
    const contentDetails = buildContentDetails(EventModel);
    createContentsRequest = {
      title: EventModel?.eventName,
      topic: EventModel?.trigger,
      language: "en",
      contentType: EventModel?.eventSolution?.type,
      contentDetails: JSON.stringify(contentDetails?.contentDetailsList),
      fieldNames: JSON.stringify(contentDetails?.fieldNamesList),
      client: "EASE",
    }
    return createContentsRequest;
  } catch (error) {
    logger.error("error in building AEM CreateContents Request = " + error);
    return null;
  }
}

/**
 * buildContentDetails: Builds Content and Field Details
 * @param EventModel
 */
export const buildContentDetails = (EventModel: EventModelState): ContentAndFieldDetails => {
  console.log("EventModel", EventModel);
  let contentDetailsList: ContentDetailsModel[] = [];
  let fieldNamesList: FieldNameModel[] = [];
  try {
    const EventModelKeys = Object.keys(EventModel?.eventSolution?.solutionDetailObject);
    console.log("EventModelKeys", EventModelKeys);
    if (EventModel && EventModelKeys.includes(CommonConstants.CONTENT_DETAILS)) {
      const contentDetailsObject = EventModel?.eventSolution?.solutionDetailObject[CommonConstants.CONTENT_DETAILS];
      console.log("contentDetailsObject", contentDetailsObject);
      const contentDetailsAttributes = Object.keys(contentDetailsObject);
      console.log("contentDetailsAttributes", contentDetailsAttributes);
      contentDetailsAttributes?.forEach((attributes) => {
        fieldNamesList.push({fieldName: attributes} as FieldNameModel);
        const joinedKey = [CommonConstants.CONTENT_DETAILS, attributes].join(".");
        contentDetailsList.push({field: EventModel.eventSolution.solutionDetailObject[joinedKey]} as ContentDetailsModel)
      })
      console.log("fieldNames", fieldNamesList);
      console.log("contentDetailsList", contentDetailsList);
    }
    return {contentDetailsList: contentDetailsList, fieldNamesList: fieldNamesList} as ContentAndFieldDetails;
  } catch (error) {
    logger.error("error in building ContentDetails and FieldNames = " + error);
    return null;
  }
}
